// src/pages1/SharedExpense.js
import DashboardCanvas from "../components1/DashboardCanvas";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import uuid from "react-uuid";
import History from "./History";
import { getSharedExpense, fetchUsers, getExpenseCategory, getCurrency, API_BASE, API_HEADERS } from "../service/APIService";
import { ThemedDatePicker, ThemedMonthPicker } from "../components1/ThemedCalendar";
import HeroTrendChart from "../components1/HeroTrendChart";
import useSheetDismiss, { SHEET_CLOSE_MS } from "../utils/useSheetDismiss";
import { toLocalYMD } from "../utils/localDate";
import CategoryTrendDrawer from "../components1/CategoryTrendDrawer";
import CategoryDonut from "../components1/CategoryDonut";
import AddChoiceSheet from "../components1/AddChoiceSheet";
import VoiceAddSheet from "../components1/VoiceAddSheet";
import { isAIConfigured } from "../service/AIService";
import AnimatedCounter from "../components1/AnimatedCounter";
import {

  getSharedExpenseCache,
  setSharedExpenseCache,
  clearSharedExpenseCache,
  dispatchSharedExpenseUpdated,
} from "../service/sharedExpenseStore";


const CHART_COLORS = [
  "#42224A", // Deep Plum
  "#EF8767", // Vibrant Coral
  "#8F659A", // Rich Mauve
  "#2B7A78", // Deep Teal
  "#E66A46", // Terracotta Coral
  "#5C3566", // Royal Plum
  "#2563EB", // Royal Blue
  "#D97706", // Amber Gold
  "#059669", // Emerald Green
  "#9333EA", // Vivid Purple
  "#DC2626", // Crimson Red
  "#0D9488", // Dark Cyan
];

const PREDEFINED_CATEGORIES = ["Groceries", "Food", "Shopping", "Transport", "Subscription", "Rent", "Fuel", "Gift", "Travel", "Saving", "Healthcare", "Entertainment", "Other"];

const getCategoryIcon = (category = "") => {
  const cat = category.toLowerCase();
  if (cat.includes("food") || cat.includes("rest") || cat.includes("drink") || cat.includes("cafe")) return "fi fi-rr-utensils";
  if (cat.includes("grocer") || cat.includes("supermarket")) return "fi fi-rr-shopping-bag";
  if (cat.includes("shop") || cat.includes("cloth") || cat.includes("shoe")) return "fi fi-rr-shop";
  if (cat.includes("travel") || cat.includes("fuel") || cat.includes("uber") || cat.includes("car") || cat.includes("transport")) return "fi fi-rr-car";
  if (cat.includes("rent") || cat.includes("home") || cat.includes("house")) return "fi fi-rr-home";
  if (cat.includes("sub") || cat.includes("stream") || cat.includes("tech") || cat.includes("apple") || cat.includes("phone")) return "fi fi-rr-laptop";
  if (cat.includes("health") || cat.includes("med") || cat.includes("doctor")) return "fi fi-rr-heart";
  if (cat.includes("gift") || cat.includes("party")) return "fi fi-rr-gift";
  if (cat.includes("save") || cat.includes("bank") || cat.includes("invest")) return "fi fi-rr-piggy-bank";
  return "fi fi-rr-receipt";
};

const SharedExpense = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = user?.email || localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest") {
        const cached = getSharedExpenseCache(userEmail);
        if (cached && Array.isArray(cached.expenses)) {
          return cached.expenses;
        }
      }
    } catch (e) {}
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [checkingSharingLink, setCheckingSharingLink] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef(null);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [showNotLinkedNotice, setShowNotLinkedNotice] = useState(true);
  const descriptionRef = useRef(null);
  const [keyboardSpacer, setKeyboardSpacer] = useState(false);
  const { closing: addSheetClosing, requestClose: closeAddSheet } = useSheetDismiss(() => setShowAddSheet(false));

  const [activeView, setActiveView] = useState("transactions");
  const [showAddSheet, setShowAddSheet] = useState(false);
  // the + offers voice or the form first; only where voice can actually run
  const [showAddChoice, setShowAddChoice] = useState(false);
  const [showVoiceSheet, setShowVoiceSheet] = useState(false);

  const [isGuest, setIsGuest] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const isReal = user && user.email && user.email !== "guest" && user.email.includes("@");
      return !isReal;
    } catch {
      return true;
    }
  });

  const handleSignInRedirect = () => {
    localStorage.setItem("lastVisitedPage", "/app1/sharedexpense");
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("guestUser");
    localStorage.setItem("isLoggedOut", "true");
    window.location.href = "/";
  };

  const [loggedInUserEmail, setLoggedInUserEmail] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [fetchedCategories, setFetchedCategories] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = user?.email || localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest") {
        const cached = getSharedExpenseCache(userEmail);
        if (cached && Array.isArray(cached.categories)) return cached.categories;
      }
    } catch (e) {}
    return [];
  });
  const [loggedinUserExpense, setLoggedinUserExpense] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = user?.email || localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest") {
        const cached = getSharedExpenseCache(userEmail);
        return cached?.sharedTarget || "";
      }
    } catch (e) {}
    return "";
  });
  const [sharedWithName, setSharedWithName] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = user?.email || localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest") {
        const cached = getSharedExpenseCache(userEmail);
        return cached?.sharedWithName || "";
      }
    } catch (e) {}
    return "";
  });
  const [groupName, setGroupName] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = user?.email || localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest") {
        const cached = getSharedExpenseCache(userEmail);
        return cached?.groupName || "";
      }
    } catch (e) {}
    return "";
  });
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [cachedTotal, setCachedTotal] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = user?.email || localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest") {
        const cached = getSharedExpenseCache(userEmail);
        if (cached && typeof cached.allTimeTotal === "number") {
          return cached.allTimeTotal;
        }
      }
    } catch (e) {}
    return null;
  });
  const [selectedCategoryTrend, setSelectedCategoryTrend] = useState(null);

  // Reference to hold current state values for cache updates
  const stateRef = useRef({
    expenses: [],
    fetchedCategories: [],
    loggedinUserExpense: "",
    sharedWithName: "",
    groupName: "",
  });

  useEffect(() => {
    stateRef.current = {
      expenses,
      fetchedCategories,
      loggedinUserExpense,
      sharedWithName,
      groupName,
    };
  }, [expenses, fetchedCategories, loggedinUserExpense, sharedWithName, groupName]);

  const syncCache = (updatedExpenses, overrideData = {}) => {
    const curEmail = email || loggedInUserEmail;
    if (!curEmail || curEmail === "guest") return;
    setSharedExpenseCache(curEmail, {
      expenses: updatedExpenses !== undefined ? updatedExpenses : stateRef.current.expenses,
      categories: overrideData.categories || stateRef.current.fetchedCategories,
      sharedTarget: overrideData.sharedTarget !== undefined ? overrideData.sharedTarget : stateRef.current.loggedinUserExpense,
      sharedWithName: overrideData.sharedWithName !== undefined ? overrideData.sharedWithName : stateRef.current.sharedWithName,
      groupName: overrideData.groupName !== undefined ? overrideData.groupName : stateRef.current.groupName,
    });
  };

  const getCategoryMonthlyTrend = (categoryName) => {
    const today = new Date();
    const result = [];

    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const monthLabel = d.toLocaleString("en-US", { month: "short" });
      const yearMonth = `${year}-${String(month).padStart(2, "0")}`;

      const monthRows = expenses
        .filter((exp) => {
          if (!exp.date) return false;
          const [expY, expM] = exp.date.split("-").map(Number);
          const matchesDate = expY === year && expM === month;
          const matchesCat = (exp.category || "OTHER").trim().toLowerCase() === (categoryName || "").trim().toLowerCase();
          return matchesDate && matchesCat;
        });

      const total = monthRows.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);

      result.push({
        month: monthLabel,
        yearMonth,
        amount: Math.round(total * 100) / 100,
        count: monthRows.length,
        items: monthRows
          .slice()
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .map((exp) => ({
            id: exp.id || `${exp.date}-${exp.description}-${exp.amount}`,
            date: exp.date,
            description: exp.description || exp.category || "Expense",
            amount: Number(exp.amount || 0),
            paidBy: exp.email || exp.userEmail || "",
          })),
      });
    }

    return result;
  };

  const getTodayDate = () => toLocalYMD(); // the phone's date, not UTC

  const [form, setForm] = useState({
    description: "",
    amount: "",
    category: PREDEFINED_CATEGORIES[0],
    date: getTodayDate(),
  });

  // Listen for FAB click event from BottomNav
  useEffect(() => {
    const handleOpenAddSheet = () => {
      if (isGuest || !loggedinUserExpense) return;
      if (isAIConfigured()) {
        setShowAddChoice(true);
      } else {
        setShowAddSheet(true);
      }
    };
    window.addEventListener("open-add-expense", handleOpenAddSheet);
  
  return () => window.removeEventListener("open-add-expense", handleOpenAddSheet);
  }, [isGuest, loggedinUserExpense]);

  // Listen for shared expense updates dispatched elsewhere in the app
  useEffect(() => {
    const handleExternalUpdate = () => {
      const curUser = email || loggedInUserEmail;
      if (curUser && curUser !== "guest") {
        getUsers(curUser);
      }
    };

    const handleManualRefresh = () => {
      const curUser = email || loggedInUserEmail;
      if (curUser && curUser !== "guest") {
        clearSharedExpenseCache(curUser);
        fetchCategories(curUser);
        getUsers(curUser);
      }
    };

    window.addEventListener("shared-expense-updated", handleExternalUpdate);
    window.addEventListener("app-manual-refresh", handleManualRefresh);

    return () => {
      window.removeEventListener("shared-expense-updated", handleExternalUpdate);
      window.removeEventListener("app-manual-refresh", handleManualRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, loggedInUserEmail]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
        const isRealUser = sessionUser && sessionUser.email && sessionUser.email !== "guest" && sessionUser.email.includes("@");

        if (isRealUser) {
          setIsGuest(false);
          setEmail(sessionUser.email);
          setLoggedInUserEmail(sessionUser.email);
          setCountry(sessionUser.country || "");
          localStorage.setItem("loggedInUserEmail", sessionUser.email);
          localStorage.removeItem("guestUser");

          // 1. FAST LOCAL STORE HYDRATION (Instant Render as earlier!)
          const cached = getSharedExpenseCache(sessionUser.email);
          if (cached && Array.isArray(cached.expenses) && cached.expenses.length > 0) {
            setExpenses(cached.expenses);
            if (typeof cached.allTimeTotal === "number") setCachedTotal(cached.allTimeTotal);
            if (cached.categories) setFetchedCategories(cached.categories);
            if (cached.sharedTarget) setLoggedinUserExpense(cached.sharedTarget);
            if (cached.sharedWithName) setSharedWithName(cached.sharedWithName);
            if (cached.groupName) setGroupName(cached.groupName);
          }

          // 2. CHECK SHARING LINK IN BACKGROUND (with small loading near sharing tile)
          setCheckingSharingLink(true);
          try {
            await Promise.all([fetchCategories(sessionUser.email), getUsers(sessionUser.email)]);
          } finally {
            if (isMounted) {
              setCheckingSharingLink(false);
            }
          }
        } else {
          // Guest mode: Shared Expense is not allowed for guest users
          setIsGuest(true);
          setLoggedInUserEmail("guest");
          setEmail("guest");
        }
      } catch (err) {
        console.error("SharedExpense initialization failed:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async (userEmail) => {
    try {
      const response = await getExpenseCategory(userEmail);
      if (Array.isArray(response)) {
        const cats = response.filter((item) => item.type === "expense-category").map((item) => item.category);
        setFetchedCategories(cats);
        syncCache(undefined, { categories: cats });
      }
    } catch (err) {
      console.warn("Could not fetch categories:", err);
    }
  };

  const getUsers = async (userEmail) => {
    setCheckingSharingLink(true);
    try {
      const response = await fetchUsers(userEmail);
      console.log("fetchUsers response:", response);

      const shared = response?.sharedWith;
      let sharedTarget = null;
      if (typeof shared === "string" && shared.trim().length > 0) {
        sharedTarget = shared.trim();
      } else if (Array.isArray(shared) && shared.length > 0) {
        sharedTarget = shared[0];
      }

      let grpName = "";
      if (response?.groupName) {
        grpName = response.groupName;
        setGroupName(grpName);
      } else {
        setGroupName("");
      }

      if (sharedTarget) {
        setLoggedinUserExpense(sharedTarget);

        // Fetch partner user profile to display their actual Name
        let resolvedNames = "";
        try {
          const rawList = sharedTarget
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
          const uniqueEmails = Array.from(new Set(rawList));
          const partnerEmails = uniqueEmails.filter(
            (m) => m !== (userEmail || "").toLowerCase()
          );

          const targetToQuery = partnerEmails.length > 0 ? partnerEmails : uniqueEmails;
          const names = await Promise.all(
            targetToQuery.map(async (pEmail) => {
              try {
                const u = await fetchUsers(pEmail);
                return u?.name || pEmail.split("@")[0];
              } catch (e) {
                return pEmail.split("@")[0];
              }
            })
          );
          resolvedNames = names.filter(Boolean).join(", ");
          if (resolvedNames) {
            setSharedWithName(resolvedNames);
          }
        } catch (nameErr) {
          console.warn("Could not resolve partner name:", nameErr);
        }

        await fetchExpenses(sharedTarget, false, {
          sharedTarget,
          sharedWithName: resolvedNames,
          groupName: grpName,
        });
      } else {
        // User is not linked to any sharing group
        setLoggedinUserExpense("");
        setSharedWithName("");
        setGroupName("");
        setExpenses([]);
        setCachedTotal(0);
        clearSharedExpenseCache(userEmail);
      }
    } catch (err) {
      console.error("Failed to fetch user or shared expenses:", err);
    } finally {
      setCheckingSharingLink(false);
    }
  };

  // Rows written a moment ago may be missing from the next read (the table is
  // eventually consistent), so keep any pending row until the server reports it.
  const pendingExpensesRef = useRef([]);

  const mergePending = (rows) => {
    if (pendingExpensesRef.current.length === 0) return rows;
    const known = new Set(rows.map((r) => r && r.id));
    pendingExpensesRef.current = pendingExpensesRef.current.filter((p) => !known.has(p.id));
    return pendingExpensesRef.current.length ? [...pendingExpensesRef.current, ...rows] : rows;
  };

  const fetchExpenses = async (targetEmail, isSilent = false, meta = {}) => {
    if (!targetEmail || targetEmail === "guest") {
      loadGuestExpenses();
      return;
    }

    if (!isSilent) setLoading(true);
    try {
      // Query active shared expenses for this group
      let response = await getSharedExpense(targetEmail);

      if (Array.isArray(response)) {
        response = mergePending(response);
        setExpenses(response);
        // Save to local store cache for instant reopening only if actively linked
        const activeUserEmail = email || loggedInUserEmail;
        const currentTarget = meta.sharedTarget !== undefined ? meta.sharedTarget : stateRef.current.loggedinUserExpense;
        if (activeUserEmail && currentTarget) {
          setSharedExpenseCache(activeUserEmail, {
            expenses: response,
            categories: stateRef.current.fetchedCategories,
            sharedTarget: currentTarget,
            sharedWithName: meta.sharedWithName !== undefined ? meta.sharedWithName : stateRef.current.sharedWithName,
            groupName: meta.groupName !== undefined ? meta.groupName : stateRef.current.groupName,
          });
        }
      }
    } catch (err) {
      console.error("Error fetching shared expenses:", err);
    } finally {
      if (!isSilent) setLoading(false);
    }
  };


  const loadGuestExpenses = () => {
    let guestShared = JSON.parse(localStorage.getItem("guestSharedExpenses"));
    if (!guestShared || guestShared.length === 0) {
      const today = new Date();
      const formatYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      const starterShared = [
        {
          id: "shared-1",
          description: "Apartment Rent (Split)",
          amount: 650.0,
          category: "RENT",
          date: formatYMD(today),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
        {
          id: "shared-2",
          description: "Costco Grocery Run",
          amount: 185.4,
          category: "GROCERIES",
          date: formatYMD(today),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
        {
          id: "shared-3",
          description: "Electricity & Wifi",
          amount: 72.5,
          category: "SUBSCRIPTION",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
        {
          id: "shared-4",
          description: "Family Dinner Out",
          amount: 94.0,
          category: "FOOD",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 3)),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
        {
          id: "shared-5",
          description: "Home Essentials",
          amount: 45.0,
          category: "SHOPPING",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 1, 12)),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
        {
          id: "shared-6",
          description: "Monthly Utilities",
          amount: 220.0,
          category: "RENT",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 2, 10)),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
        {
          id: "shared-7",
          description: "Shared Road Trip Fuel",
          amount: 140.0,
          category: "TRAVEL",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 3, 5)),
          email: "guest",
          expensetype: "shared",
          sharedWith: "family@example.com",
        },
      ];
      localStorage.setItem("guestSharedExpenses", JSON.stringify(starterShared));
      guestShared = starterShared;
    }
    setExpenses(guestShared);
    setSharedWithName("Family Member");
  };

  // Sort categories by frequency of usage in expenses (most frequent first)
  const sortedCategories = useMemo(() => {
    const counts = {};
    expenses.forEach((exp) => {
      const cat = (exp.category || "").trim();
      if (cat) {
        counts[cat.toLowerCase()] = (counts[cat.toLowerCase()] || 0) + 1;
      }
    });

    const all = Array.from(new Set([...PREDEFINED_CATEGORIES, ...fetchedCategories]));
    return all.sort((a, b) => {
      const countA = counts[a.toLowerCase()] || 0;
      const countB = counts[b.toLowerCase()] || 0;
      if (countB !== countA) return countB - countA;
      return a.localeCompare(b);
    });
  }, [expenses, fetchedCategories]);

  const combinedCategories = sortedCategories;

  // Frequently used categories (first 3 lines in 2-column layout: 6 items) vs all
  const FREQUENT_LIMIT = 6;
  const visibleCategories = useMemo(() => {
    if (showAllCategories) return sortedCategories;
    const top = sortedCategories.slice(0, FREQUENT_LIMIT);
    // Ensure currently selected category is included in the visible list while keeping 4 items
    if (form.category && !top.some((c) => c.toLowerCase() === form.category.toLowerCase())) {
      if (top.length >= FREQUENT_LIMIT) {
        return [...top.slice(0, FREQUENT_LIMIT - 1), form.category];
      } else {
        return [...top, form.category];
      }
    }
    return top;
  }, [sortedCategories, showAllCategories, form.category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "amount" && value < 0 ? 0 : value,
    }));
  };

  const handleCategorySelect = (categoryName) => {
    setForm((prev) => ({ ...prev, category: categoryName }));
  };

  const showSavedNotification = () => {
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2000);
  };

  const isExpenseFormValid = Boolean(form.description.trim()) && Boolean(form.amount) && Number(form.amount) > 0;

  // iOS shrinks the viewport for the keyboard but leaves the focused field where it
  // was; pull it back into the middle of what is still visible.
  const keepFieldVisible = (e) => {
    const el = e.target;
    const sheet = el.closest && el.closest(".bottom-sheet-content");
    if (!sheet) return;
    setKeyboardSpacer(true); // lets even the first field scroll up to the keyboard
    setTimeout(() => {
      try {
        // never scrollIntoView here: on iOS that scrolls the page as well and
        // drags the pinned sheet up to the top edge.
        const elRect = el.getBoundingClientRect();
        const elTop = elRect.top - sheet.getBoundingClientRect().top;
        // sit the field just above the bottom edge of the sheet, i.e. just above
        // the keyboard, rather than leaving it stranded at the top.
        const target = sheet.scrollTop + elTop - (sheet.clientHeight - elRect.height - 24);
        sheet.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
        window.scrollTo(0, 0); // undo any scroll iOS did to reveal the field
      } catch {
        /* older browsers ignore the options object */
      }
    }, 300);
  };

  const releaseFieldSpacer = () => {
    // collapse once focus has left every field in the sheet
    setTimeout(() => {
      const active = document.activeElement;
      if (!active || !active.closest || !active.closest(".bottom-sheet-content")) {
        setKeyboardSpacer(false);
      }
    }, 150);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (loggedInUserEmail !== "guest" && !loggedinUserExpense) {
      alert("You are not currently linked to any family sharing group. Please link with a member in Settings first.");
      navigate("/app1/settings");
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    if (!form.description) {
      alert("Please enter an expense description.");
      return;
    }
    await saveExpense(form, closeAddSheet);
  };

  // Shared by the form and by the voice sheet, so a spoken expense is written
  // exactly the way a typed one is.
  const saveExpense = async (fields, dismissSheet) => {
    const newExpense = {
      id: uuid(),
      type: "expense",
      description: fields.description,
      amount: parseFloat(fields.amount),
      category: String(fields.category || "").toUpperCase(),
      date: fields.date,
      email: email,
      expensetype: "shared",
      sharedWith: loggedinUserExpense || "",
    };

    setLoading(true);
    try {
      if (loggedInUserEmail !== "guest") {
        const res = await fetch(`${API_BASE}/items`, {
          method: "POST",
          headers: API_HEADERS,
          body: JSON.stringify(newExpense),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        showSavedNotification();
        pendingExpensesRef.current = [newExpense, ...pendingExpensesRef.current];
        const updatedList = [newExpense, ...expenses];
        setExpenses(updatedList);
        syncCache(updatedList);
        dispatchSharedExpenseUpdated();
        fetchExpenses(loggedinUserExpense || email, true);
      } else {
        const guestShared = JSON.parse(localStorage.getItem("guestSharedExpenses")) || [];
        guestShared.unshift(newExpense);
        localStorage.setItem("guestSharedExpenses", JSON.stringify(guestShared));
        loadGuestExpenses();
        showSavedNotification();
      }
      setForm({
        description: "",
        amount: "",
        category: combinedCategories[0] || PREDEFINED_CATEGORIES[0],
        date: getTodayDate(),
      });
      dismissSheet();
      // bring the chart back into view so the new expense is visible on it
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
          window.scrollTo(0, 0);
        }
      }, SHEET_CLOSE_MS + 160); // after the sheet has slid away and unlocked the page
    } catch (err) {
      console.error("Failed to save shared expense:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalExpense = useMemo(() => {
    const computed = expenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    if (typeof cachedTotal === "number" && cachedTotal > computed) {
      return cachedTotal;
    }
    return computed;
  }, [expenses, cachedTotal]);

  // Mini bar chart for hero card — dynamic multi-month history with horizontal scrolling
  const miniBarData = useMemo(() => {
    const today = new Date();
    // Dynamically calculate months to show: at least 12 months, or up to the earliest recorded expense
    let monthsBack = 11;
    expenses.forEach((exp) => {
      if (exp.date) {
        const [y, m] = exp.date.split("-").map(Number);
        const diffMonths = (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m);
        if (diffMonths > monthsBack && diffMonths < 60) {
          monthsBack = diffMonths;
        }
      }
    });

    const months = [];
    for (let i = monthsBack; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const monthStr = `${year}-${String(month).padStart(2, "0")}`;
      const label = d.toLocaleString("en-US", { month: "short" });

      const total = expenses
        .filter((exp) => {
          if (!exp.date) return false;
          const [expY, expM] = exp.date.split("-").map(Number);
          return expY === year && expM === month;
        })
        .reduce((sum, exp) => sum + Number(exp.amount || 0), 0);

      months.push({
        label,
        yearMonth: monthStr,
        total,
        isCurrent: i === 0,
      });
    }

    const maxTotal = Math.max(...months.map((m) => m.total), 1);
    return months.map((m) => ({
      ...m,
      heightPercent: Math.max(12, Math.round((m.total / maxTotal) * 100)),
    }));
  }, [expenses]);

  const monthOptions = useMemo(() => {
    const months = new Set();
    expenses.forEach((expense) => {
      if (expense.date) {
        const [year, month] = expense.date.split("-");
        months.add(`${year}-${month}`);
      }
    });

    const options = Array.from(months)
      .sort((a, b) => new Date(b) - new Date(a))
      .map((ym) => {
        const [year, month] = ym.split("-").map(Number);
        const date = new Date(year, month - 1, 1);
        return {
          value: ym,
          label: date.toLocaleString("en-US", { month: "long", year: "numeric" }),
        };
      });

    return [{ value: "current", label: "Current Month" }, { value: "all", label: "All Time" }, ...options];
  }, [expenses]);

  const reportFilteredExpenses = useMemo(() => {
    if (selectedMonth === "all") return expenses;

    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;

    const [filterYear, filterMonth] = selectedMonth === "current" ? [curYear, curMonth] : selectedMonth.split("-").map(Number);

    return expenses.filter((expense) => {
      if (!expense.date) return false;
      const [y, m] = expense.date.split("-").map(Number);
      return y === filterYear && m === filterMonth;
    });
  }, [selectedMonth, expenses]);

  const reportTotal = useMemo(() => {
    return reportFilteredExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  }, [reportFilteredExpenses]);

  // The month the report is filtered to, as YYYY-MM — the category table
  // highlights it and opens on it. Null while viewing All Time.
  const activeYearMonth = useMemo(() => {
    if (selectedMonth === "all") return null;
    if (selectedMonth === "current") {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    }
    return selectedMonth;
  }, [selectedMonth]);

  const categoryBreakdown = useMemo(() => {
    if (reportTotal === 0) return [];
    const grouped = reportFilteredExpenses.reduce((acc, exp) => {
      const cat = exp.category || "OTHER";
      acc[cat] = (acc[cat] || 0) + Number(exp.amount || 0);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], index) => {
        const percent = Math.round((value / reportTotal) * 1000) / 10;
        return {
          name,
          value,
          percent,
          color: CHART_COLORS[index % CHART_COLORS.length],
        };
      });
  }, [reportFilteredExpenses, reportTotal]);

  if (isGuest) {
    return (
      <div className="shared-guest-locked-container">
        <DashboardCanvas theme="shared" />
        <div className="shared-guest-locked-card">
          <div className="shared-guest-locked-icon-badge">
            <i className="fi fi-rr-users-alt"></i>
            <span className="shared-guest-locked-badge">
              <i className="fi fi-rr-lock"></i>
            </span>
          </div>

          <h3 className="shared-guest-locked-title">Sign In Required</h3>
          <p className="shared-guest-locked-subtitle">
            Shared expense tracking and family sharing require signing in with your email account to link with members and sync in real time across devices.
          </p>

          <div className="shared-guest-locked-features">
            <div className="shared-guest-feature-item">
              <div className="shared-guest-feature-icon">
                <i className="fi fi-rr-refresh"></i>
              </div>
              <div className="shared-guest-feature-text">
                <span className="shared-guest-feature-title">Real-Time Sync</span>
                <span className="shared-guest-feature-desc">Split household bills and update balances automatically.</span>
              </div>
            </div>

            <div className="shared-guest-feature-item">
              <div className="shared-guest-feature-icon">
                <i className="fi fi-rr-users"></i>
              </div>
              <div className="shared-guest-feature-text">
                <span className="shared-guest-feature-title">Family & Partner Sharing</span>
                <span className="shared-guest-feature-desc">Link with family members or partners directly in Settings.</span>
              </div>
            </div>

            <div className="shared-guest-feature-item">
              <div className="shared-guest-feature-icon">
                <i className="fi fi-rr-cloud-upload"></i>
              </div>
              <div className="shared-guest-feature-text">
                <span className="shared-guest-feature-title">Cloud Backup</span>
                <span className="shared-guest-feature-desc">Your shared transaction history is safely backed up.</span>
              </div>
            </div>
          </div>

          <div className="shared-guest-locked-actions">
            <button
              type="button"
              className="btn-shared-signin"
              onClick={handleSignInRedirect}
            >
              <i className="fi fi-rr-sign-in-alt"></i>
              <span>Sign In to Continue</span>
            </button>

            <button
              type="button"
              className="btn-shared-back-home"
              onClick={() => navigate("/app1/home")}
            >
              <span>Back to Personal Expenses</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 pt-2">
      <DashboardCanvas theme="shared" />
      {/* Not-linked notice, shown as a popup so it does not sit inside the header block */}
      {!isGuest && !loggedinUserExpense && !checkingSharingLink && showNotLinkedNotice && (
        <div className="not-linked-toast" role="status">
          <div className="not-linked-toast-icon">
            <i className="fi fi-rr-users"></i>
          </div>
          <div className="not-linked-toast-text">
            <div className="not-linked-toast-title">Family Not Linked</div>
            <div className="not-linked-toast-sub">Link with family members in Settings to sync and share household expenses.</div>
          </div>
          <div className="not-linked-toast-actions">
            <button type="button" className="not-linked-toast-cta" onClick={() => navigate("/app1/settings")}>
              Link in Settings
            </button>
            <button
              type="button"
              className="not-linked-toast-close"
              onClick={() => setShowNotLinkedNotice(false)}
              aria-label="Dismiss"
            >
              <i className="fi fi-rr-cross-small"></i>
            </button>
          </div>
        </div>
      )}

      {/* View Switcher */}
      <div className="segmented-control">
        <div
          className="segmented-slider"
          style={{
            transform: activeView === "report" ? "translateX(100%)" : "translateX(0%)",
          }}
          aria-hidden="true"
        />
        <button className={`segmented-btn ${activeView === "transactions" ? "active" : ""}`} onClick={() => setActiveView("transactions")}>
          Shared Feed
        </button>
        <button className={`segmented-btn ${activeView === "report" ? "active" : ""}`} onClick={() => setActiveView("report")}>
          Shared Analytics
        </button>
      </div>

      {activeView === "transactions" ? (
        <div>
          {/* Hero Outcome Card */}
          <div className="hero-balance-card">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  className="d-flex align-items-center gap-2 mb-2 flex-nowrap"
                  style={{ minWidth: 0 }}
                >
                  <span
                    className="hero-balance-label mb-0 d-inline-flex align-items-center gap-1"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      flexShrink: 1,
                    }}
                    title={groupName ? `${groupName} Outcome` : "Outcome"}
                  >
                    <i
                      className="fi fi-rr-users-alt"
                      style={{ fontSize: "13px", opacity: 0.85, flexShrink: 0 }}
                      aria-hidden="true"
                    ></i>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {groupName ? `${groupName} Outcome` : "Outcome"}
                    </span>
                  </span>
                  <div style={{ flexShrink: 0 }}>
                    <ThemedMonthPicker
                      value={selectedMonth}
                      onChange={setSelectedMonth}
                      options={monthOptions}
                      variant="glass"
                    />
                  </div>
                </div>
                <div className="hero-balance-amount">
                  <AnimatedCounter
                    value={reportTotal}
                    currency={getCurrency(country)}
                  />
                </div>
              </div>

              <button
                className="header-icon-btn"
                style={{
                  background: !loggedinUserExpense ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.15)",
                  color: !loggedinUserExpense ? "rgba(255, 255, 255, 0.35)" : "#FFFFFF",
                  border: "none",
                  cursor: !loggedinUserExpense ? "not-allowed" : "pointer",
                  opacity: !loggedinUserExpense ? 0.45 : 1,
                  flexShrink: 0,
                }}
                disabled={!loggedinUserExpense}
                onClick={() => {
                  if (!loggedinUserExpense) {
                    return;
                  }
                  setShowAddSheet(true);
                }}
                title={!loggedinUserExpense ? "Family not linked - link with a member in Settings to add expenses" : "Add Shared Expense"}
              >
                <i className="fi fi-rr-plus"></i>
              </button>
            </div>

            {/* Mini Bar + Curved Trend Line Chart */}
            <HeroTrendChart data={miniBarData} currency={getCurrency(country)} selectedMonth={selectedMonth} onSelectMonth={(ym) => setSelectedMonth(ym)} />
          </div>

          {/* Quick Metrics */}
          <div className="metrics-row">
            <div className="metric-card">
              <div className="metric-icon-wrap metric-icon-expense">
                <i className="fi fi-rr-wallet"></i>
              </div>
              <div className="metric-info">
                <span className="metric-label">Total Shared</span>
                <span className="metric-value">
                  {getCurrency(country)}
                  {totalExpense.toFixed(2)}
                </span>
              </div>
            </div>

            <div
              className="metric-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/app1/settings")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/app1/settings");
              }}
              style={{ cursor: "pointer" }}
              title="Manage family sharing in Settings"
            >
              <div className="metric-icon-wrap metric-icon-income">
                <i className="fi fi-rr-users"></i>
              </div>
              <div className="metric-info">
                <div className="d-flex align-items-center gap-1">
                  <span className="metric-label mb-0">Shared With</span>
                  {checkingSharingLink && (
                    <span
                      className="spinner-border"
                      role="status"
                      style={{
                        width: "9px",
                        height: "9px",
                        borderWidth: "1.5px",
                        borderColor: "var(--accent-coral)",
                        borderRightColor: "transparent",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                      title="Verifying sharing status..."
                    ></span>
                  )}
                </div>
                <span
                  className="metric-value text-truncate"
                  style={{
                    maxWidth: "125px",
                    display: "inline-block",
                    fontSize: "13px",
                    color: !loggedinUserExpense ? "#DC2626" : undefined,
                    fontWeight: !loggedinUserExpense ? 700 : undefined,
                  }}
                  title={sharedWithName || loggedinUserExpense || "Not Linked"}
                >
                  {sharedWithName || (loggedinUserExpense ? loggedinUserExpense.split("@")[0] : "Not Linked")}
                </span>
              </div>
            </div>
          </div>



          {/* Transactions List */}
          <History
            expenses={expenses}
            country={country}
            isGuest={loggedInUserEmail === "guest"}
            onChanged={async () => {
              if (loggedInUserEmail === "guest") {
                loadGuestExpenses();
              } else {
                await fetchExpenses(loggedinUserExpense || email, true);
                dispatchSharedExpenseUpdated();
              }
            }}
            categories={combinedCategories}
          />
        </div>
      ) : (
        /* Report View */
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3 dash-report-head">
            <div>
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                Analytics
              </h5>
              <small className="text-muted" style={{ fontSize: "11.5px" }}>
                Group expenses by category
              </small>
            </div>

            <ThemedMonthPicker value={selectedMonth} onChange={setSelectedMonth} options={monthOptions} variant="light" showArrows={true} />
          </div>

          {/* Interactive Donut Chart */}
          <CategoryDonut
            data={categoryBreakdown}
            total={reportTotal}
            currency={getCurrency(country)}
          />

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold mb-0" style={{ fontSize: "15px", color: "var(--primary-dark)" }}>
                  Category Breakdown
                </h6>
                <small className="text-muted" style={{ fontSize: "11px" }}>
                  Tap any category to view monthly curved trend
                </small>
              </div>
            </div>

            {categoryBreakdown.length === 0 ? (
              <p className="text-muted small text-center py-4">No expenses recorded for this period.</p>
            ) : (
              categoryBreakdown.map((cat, idx) => {
                const iconClass = getCategoryIcon(cat.name);
                const isExpanded = selectedCategoryTrend === cat.name;
                const trendData = isExpanded ? getCategoryMonthlyTrend(cat.name) : [];
                const gradId = `catGrad_${idx}_${encodeURIComponent(cat.name).replace(/[^a-zA-Z0-9]/g, "_")}`;

                return (
                  <div key={idx} className={`category-report-card ${isExpanded ? "expanded" : ""}`} onClick={() => setSelectedCategoryTrend((prev) => (prev === cat.name ? null : cat.name))}>
                    <div className="category-report-header">
                      <div className="category-report-title">
                        <div
                          className="category-icon-circle"
                          style={{
                            width: "36px",
                            height: "36px",
                            fontSize: "16px",
                            backgroundColor: `${cat.color}15`,
                            color: cat.color,
                          }}
                        >
                          <i className={iconClass}></i>
                        </div>
                        <div>
                          <div style={{ lineHeight: 1.2 }}>{cat.name}</div>
                          <span className="category-report-percent">{cat.percent}% of total</span>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <div className="category-report-amount">
                          {getCurrency(country)}
                          {cat.value.toFixed(2)}
                        </div>
                        <i
                          className={`fi fi-rr-angle-small-${isExpanded ? "up" : "down"}`}
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "16px",
                            transition: "transform 0.2s ease",
                          }}
                        ></i>
                      </div>
                    </div>

                    <div className="category-progress-track">
                      <div
                        className="category-progress-fill"
                        style={{
                          width: `${cat.percent}%`,
                          backgroundColor: cat.color,
                        }}
                      ></div>
                    </div>

                    {/* Expandable Monthly Curved Line Trend Graph */}
                    {isExpanded && (
                      <CategoryTrendDrawer
                        cat={cat}
                        trendData={trendData}
                        activeYearMonth={activeYearMonth}
                        currency={getCurrency(country)}
                        gradId={gradId}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Add Shared Expense Bottom Sheet */}
      {showAddChoice && !isGuest && !!loggedinUserExpense && (
        <AddChoiceSheet
          example="50 dollars shared groceries at Kroger"
          onVoice={() => {
            setShowAddChoice(false);
            setShowVoiceSheet(true);
          }}
          onManual={() => {
            setShowAddChoice(false);
            setShowAddSheet(true);
          }}
          onClose={() => setShowAddChoice(false)}
        />
      )}

      {showVoiceSheet && !isGuest && !!loggedinUserExpense && (
        <VoiceAddSheet
          categories={combinedCategories}
          currency={getCurrency(country)}
          scope="shared"
          todayLocal={getTodayDate()}
          onSave={(draft) => saveExpense(draft, () => setShowVoiceSheet(false))}
          onClose={() => setShowVoiceSheet(false)}
          onManual={() => {
            setShowVoiceSheet(false);
            setShowAddSheet(true);
          }}
        />
      )}

      {showAddSheet && !isGuest && !!loggedinUserExpense && (
        <div className="bottom-sheet-overlay" onClick={closeAddSheet}>
          <div className={`bottom-sheet-content${addSheetClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeAddSheet}></div>
            <button type="button" className="bottom-sheet-close-corner" onClick={closeAddSheet} aria-label="Close" title="Close">
              <i className="fi fi-rr-cross"></i>
            </button>

            <div className="mb-3">
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                Add Shared Expense
              </h5>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="modern-input-group mb-3">
                <label>Amount{getCurrency(country) ? ` (${getCurrency(country)})` : ""}</label>
                <input
                  type="number"
                  name="amount"
                  placeholder="0.00"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  className="modern-input modern-input-amount"
                  value={form.amount}
                  onChange={handleInputChange}
                  onFocus={keepFieldVisible}
                  onBlur={releaseFieldSpacer}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (descriptionRef.current) descriptionRef.current.focus();
                    }
                  }}
                  enterKeyHint="next"
                  autoFocus
                  required
                />
              </div>

              {/* Description Input (required) */}
              <div className="modern-input-group mb-3">
                <label>
                  Description <span style={{ color: "#E5484D" }}>*</span>
                </label>
                <input
                  ref={descriptionRef}
                  type="text"
                  name="description"
                  placeholder="e.g. Dinner with Friends, Electric Bill"
                  className={`modern-input${descriptionTouched && !form.description.trim() ? " is-invalid-field" : ""}`}
                  value={form.description}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setDescriptionTouched(true);
                    releaseFieldSpacer();
                  }}
                  onFocus={keepFieldVisible}
                  required
                />
                {descriptionTouched && !form.description.trim() && (
                  <small style={{ color: "#E5484D" }}>Description is required</small>
                )}
              </div>

              <div className="modern-input-group mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>
                    Category {!showAllCategories && <span style={{ fontWeight: 400, opacity: 0.8 }}>(Frequently Used)</span>}
                  </label>
                  {sortedCategories.length > FREQUENT_LIMIT && (
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0"
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--accent-coral)",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowAllCategories(!showAllCategories)}
                    >
                      {showAllCategories ? "Show Less ▴" : `Show All (${sortedCategories.length}) ▾`}
                    </button>
                  )}
                </div>

                <div className="category-2col-grid">
                  {visibleCategories.map((cat, idx) => {
                    const isSelected = form.category.toLowerCase() === cat.toLowerCase();
                    return (
                      <button key={idx} type="button" className={`category-2col-btn ${isSelected ? "active" : ""}`} onClick={() => handleCategorySelect(cat)}>
                        <i className={getCategoryIcon(cat)}></i>
                        <span>{cat}</span>
                      </button>
                    );
                  })}

                  {!showAllCategories && sortedCategories.length > FREQUENT_LIMIT && (
                    <button type="button" className="category-2col-more-btn" onClick={() => setShowAllCategories(true)} title="Show all categories">
                      <span>+ More ({sortedCategories.length - FREQUENT_LIMIT})</span>
                      <i className="fi fi-rr-angle-small-down"></i>
                    </button>
                  )}

                  {showAllCategories && sortedCategories.length > FREQUENT_LIMIT && (
                    <button type="button" className="category-2col-more-btn" onClick={() => setShowAllCategories(false)} title="Show fewer categories">
                      <span>Show Less</span>
                      <i className="fi fi-rr-angle-small-up"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Themed Date Picker */}
              <div className="modern-input-group mb-4">
                <ThemedDatePicker value={form.date} onChange={handleInputChange} name="date" label="Date" required />
              </div>

              <button type="submit" className="btn-coral-modern" disabled={loading || !isExpenseFormValid}>
                {saved ? (
                  <>
                    <i className="fi fi-rr-check me-2"></i> Saved Successfully!
                  </>
                ) : loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i> Adding...
                  </>
                ) : (
                  <>
                    <i className="fi fi-rr-plus me-2"></i> Save Shared Expense
                  </>
                )}
              </button>

              {/* Room to scroll the focused field above the keyboard */}
              <div aria-hidden="true" style={{ height: keyboardSpacer ? "42vh" : 0, transition: "height 0.2s ease" }} />
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedExpense;
